import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { Bookmark, Heart, StickyNote, Search, Trash2, Plus, Rocket, Wand2, Star, ExternalLink, Folder, Clock } from "lucide-react";
import { Layout } from "../components/Layout";
import { LogoBadge } from "../components/LogoBadge";
import { BuilderLogo } from "../components/BuilderLogo";
import { PageRocket } from "../components/PageRocket";
import { StartupCard } from "../components/StartupCard";
import { AISavedNotebook } from "../components/ai/AISavedNotebook";
import { NotebookCollections } from "../components/notebook/NotebookCollections";
import { NotebookHistory } from "../components/notebook/NotebookHistory";
import { useNotebook } from "../hooks/useNotebook";
import { useAuth } from "../hooks/useAuth";
import { usePro } from "../hooks/usePro";
import { fetchMyStartups, aiListSaved, aiDeleteSaved, aiUpdateSaved, resourcesFavorites, resourcesToggleFavorite } from "../lib/api";

export default function StartupNotebook() {
  const navigate = useNavigate();
  const { data, toggleSave, toggleFavorite, addNote, deleteNote } = useNotebook();
  const { user } = useAuth();
  const { attempt } = usePro();
  const [tab, setTab] = useState("ideas");
  const [q, setQ] = useState("");
  const [noteText, setNoteText] = useState("");
  const [myStartups, setMyStartups] = useState([]);
  const [aiSaved, setAiSaved] = useState([]);
  const [resourceFavs, setResourceFavs] = useState([]);

  const loadResourceFavs = useCallback(() => {
    if (!user) { setResourceFavs([]); return; }
    resourcesFavorites().then((d) => setResourceFavs(d.tools || [])).catch(() => setResourceFavs([]));
  }, [user]);
  useEffect(() => { loadResourceFavs(); }, [loadResourceFavs]);
  const removeResourceFav = async (id) => {
    setResourceFavs((p) => p.filter((t) => t.id !== id));
    try { await resourcesToggleFavorite(id, false); } catch { loadResourceFavs(); }
  };

  const loadAiSaved = useCallback(() => {
    if (!user) { setAiSaved([]); return; }
    aiListSaved().then(setAiSaved).catch(() => setAiSaved([]));
  }, [user]);

  useEffect(() => {
    if (!user) { setMyStartups([]); return; }
    fetchMyStartups().then(setMyStartups).catch(() => setMyStartups([]));
  }, [user]);
  useEffect(() => { loadAiSaved(); }, [loadAiSaved]);

  const deleteAi = async (id) => { setAiSaved((p) => p.filter((x) => x.id !== id)); try { await aiDeleteSaved(id); } catch { loadAiSaved(); } };
  const updateAi = (id, dataObj) => aiUpdateSaved(id, dataObj);

  const TABS = [
    { key: "ideas", label: "My Ideas", icon: Bookmark, count: data.saved.length },
    { key: "collections", label: "Collections", icon: Folder },
    { key: "history", label: "History", icon: Clock },
    { key: "startups", label: "My Startups", icon: Rocket, count: myStartups.length },
    { key: "ai", label: "AI Resources", icon: Wand2, count: aiSaved.length },
    { key: "favorites", label: "Favorites", icon: Heart, count: data.favorites.length + resourceFavs.length },
    { key: "notes", label: "Notes", icon: StickyNote, count: data.notes.length },
  ];

  const list = tab === "ideas" ? data.saved : data.favorites;
  const filtered = list.filter(
    (b) =>
      (b.title || "").toLowerCase().includes(q.toLowerCase()) ||
      (b.category_name || "").toLowerCase().includes(q.toLowerCase())
  );

  const handleAddNote = (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    attempt("note", () => {
      addNote({ text: noteText.trim() });
      setNoteText("");
    });
  };

  return (
    <Layout hideFooter>
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[calc(100vh-5rem)]">
        {/* Sidebar */}
        <aside className="bg-[#0F0F0F] text-white p-8 flex flex-col gap-8" data-testid="startup-notebook-sidebar">
          <div>
            <h2 className="font-display text-2xl font-bold">Startup Notebook</h2>
            <p className="text-sm text-[#A1A1AA] mt-1">Save, organize & revisit your ideas.</p>
          </div>
          <nav className="flex flex-col gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`notebook-tab-${t.key}`}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl font-medium transition-colors duration-200 ${
                    active ? "bg-white/10 text-white" : "text-[#A1A1AA] hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Icon size={18} className={active ? "text-[#D4AF37]" : ""} /> {t.label}
                  </span>
                  {typeof t.count === "number" && (
                    <span className="text-xs bg-white/10 rounded-full px-2 py-0.5">{t.count}</span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <div className="bg-[#F7F7F7] p-8 md:p-12 relative">
          <PageRocket className="hidden lg:block absolute top-8 right-10 w-28 z-0" />
          {tab !== "notes" && tab !== "startups" && tab !== "ai" && tab !== "collections" && tab !== "history" && (
            <div className="flex items-center gap-3 bg-white rounded-full border border-gray-100 px-5 py-3 max-w-md mb-8 shadow-sm">
              <Search size={18} className="text-[#687280]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search saved ideas..."
                className="w-full text-sm outline-none bg-transparent placeholder-[#9CA3AF]"
                data-testid="notebook-search-input"
              />
            </div>
          )}

          <h1 className="font-display text-2xl font-bold text-[#0F0F0F] mb-6">
            {TABS.find((t) => t.key === tab).label}
          </h1>

          {tab === "collections" ? (
            <NotebookCollections />
          ) : tab === "history" ? (
            <NotebookHistory />
          ) : tab === "ai" ? (            aiSaved.length === 0 ? (
              <Empty
                text="Nothing saved from AI Resources yet. Generate a roadmap, tool stack or workflow and save it to find it here."
                cta
                onCta={() => navigate("/ai-resources")}
                ctaLabel="Open AI Resources"
              />
            ) : (
              <AISavedNotebook items={aiSaved} onDelete={deleteAi} onUpdate={updateAi} />
            )
          ) : tab === "startups" ? (
            myStartups.length === 0 ? (
              <Empty
                text="You haven't purchased any Ready-to-Build Startups yet. Browse the marketplace and make one your own."
                cta
                onCta={() => navigate("/apps")}
                ctaLabel="Browse Startups"
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-5" data-testid="my-startups-grid">
                {myStartups.map((s, i) => <StartupCard key={s.id} startup={s} index={i} gated={false} />)}
              </div>
            )
          ) : tab === "notes" ? (
            <div className="max-w-2xl">
              <form onSubmit={handleAddNote} className="flex gap-3 mb-8">
                <input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write a personal note or idea..."
                  className="flex-1 bg-white rounded-full border border-gray-100 px-5 py-3 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  data-testid="notebook-note-input"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors"
                  data-testid="notebook-add-note-btn"
                >
                  <Plus size={16} /> Add
                </button>
              </form>
              {data.notes.length === 0 ? (
                <Empty text="No notes yet. Capture your first idea above." />
              ) : (
                <Virtuoso
                  useWindowScroll
                  data={data.notes}
                  computeItemKey={(i, n) => n.id}
                  itemContent={(i, n) => (
                    <div
                      className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start justify-between gap-4 mb-3"
                      data-testid={`note-${n.id}`}
                    >
                      <div>
                        {n.businessName && (
                          <p className="text-xs font-medium text-[#D4AF37] mb-1">on {n.businessName}</p>
                        )}
                        <p className="text-sm text-[#0F0F0F] leading-relaxed">{n.text}</p>
                      </div>
                      <button
                        onClick={() => deleteNote(n.id)}
                        className="text-[#687280] hover:text-red-500 transition-colors shrink-0"
                        data-testid={`delete-note-${n.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                />
              )}
            </div>
          ) : tab === "favorites" ? (
            (resourceFavs.length === 0 && data.favorites.length === 0) ? (
              <Empty
                text="No favorites yet. Tap the ⭐ on any tool in the Founder Resource Library, or heart a business idea, to save it here."
                cta
                onCta={() => navigate("/")}
                ctaLabel="Explore Resources"
              />
            ) : (
              <div className="space-y-8 max-w-3xl">
                {resourceFavs.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-[#0F0F0F] mb-3">Saved Resources <span className="text-[#9aa1ab]">({resourceFavs.length})</span></p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="notebook-resource-favs">
                      {resourceFavs.map((t) => (
                        <div key={t.id} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-3 hover:shadow-[0_12px_30px_rgb(0,0,0,0.06)] transition-shadow" data-testid={`resource-fav-item-${t.id}`}>
                          <BuilderLogo url={t.url} name={t.name} size={40} />
                          <div className="flex-1 min-w-0">
                            <p className="font-display font-semibold text-[#0F0F0F] text-sm truncate">{t.name}</p>
                            <a href={t.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#B8901F] hover:underline">Open Website <ExternalLink size={11} /></a>
                          </div>
                          <button onClick={() => removeResourceFav(t.id)} className="text-[#D4AF37] hover:text-red-500 transition-colors shrink-0" data-testid={`remove-resource-fav-${t.id}`} title="Remove">
                            <Star size={18} className="fill-[#D4AF37]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {data.favorites.length > 0 && (
                  <div>
                    <p className="text-sm font-bold text-[#0F0F0F] mb-3">Saved Business Ideas <span className="text-[#9aa1ab]">({data.favorites.length})</span></p>
                    <div className="space-y-3">
                      {data.favorites.filter((b) => (b.title || "").toLowerCase().includes(q.toLowerCase())).map((b) => (
                        <div key={b.id} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4 hover:shadow-[0_12px_30px_rgb(0,0,0,0.06)] transition-shadow" data-testid={`saved-item-${b.id}`}>
                          <LogoBadge size="w-12 h-12" rounded="rounded-xl" />
                          <button onClick={() => navigate(`/business/${b.id}`)} className="flex-1 text-left">
                            <p className="font-display font-semibold text-[#0F0F0F]">{b.title}</p>
                            <p className="text-xs font-medium text-[#687280] mt-0.5">{b.category_name}</p>
                          </button>
                          <button onClick={() => toggleFavorite(b)} className="text-[#D4AF37] hover:text-red-500 transition-colors shrink-0" data-testid={`remove-saved-${b.id}`} title="Remove">
                            <Heart size={20} className="fill-red-500 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          ) : filtered.length === 0 ? (
            <Empty
              text={
                list.length === 0
                  ? `No ${tab === "ideas" ? "saved ideas" : "favorites"} yet. Explore and save ideas that inspire you.`
                  : "No results match your search."
              }
              cta={list.length === 0}
              onCta={() => navigate("/business-ideas")}
            />
          ) : (
            <div className="max-w-3xl">
              <Virtuoso
                useWindowScroll
                data={filtered}
                computeItemKey={(i, b) => b.id}
                itemContent={(i, b) => (
                  <div
                    className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4 hover:shadow-[0_12px_30px_rgb(0,0,0,0.06)] transition-shadow mb-3"
                    data-testid={`saved-item-${b.id}`}
                  >
                    <LogoBadge size="w-12 h-12" rounded="rounded-xl" />
                    {b.ai ? (
                      <div className="flex-1 text-left">
                        <p className="font-display font-semibold text-[#0F0F0F]">{b.title}</p>
                        <p className="text-xs font-medium text-[#687280] mt-0.5">{b.category_name}</p>
                      </div>
                    ) : (
                      <button onClick={() => navigate(`/business/${b.id}`)} className="flex-1 text-left">
                        <p className="font-display font-semibold text-[#0F0F0F]">{b.title}</p>
                        <p className="text-xs font-medium text-[#687280] mt-0.5">{b.category_name}</p>
                      </button>
                    )}
                    <button
                      onClick={() => (tab === "ideas" ? toggleSave(b) : toggleFavorite(b))}
                      className="text-[#D4AF37] hover:text-red-500 transition-colors shrink-0"
                      data-testid={`remove-saved-${b.id}`}
                      title="Remove"
                    >
                      {tab === "ideas" ? <Bookmark size={20} className="fill-[#D4AF37]" /> : <Heart size={20} className="fill-red-500 text-red-500" />}
                    </button>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const Empty = ({ text, cta, onCta, ctaLabel = "Browse Ideas" }) => (
  <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 px-8 text-center max-w-2xl">
    <p className="text-[#687280]">{text}</p>
    {cta && (
      <button
        onClick={onCta}
        className="mt-5 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors"
        data-testid="empty-browse-btn"
      >
        {ctaLabel}
      </button>
    )}
  </div>
);
